import { useState, useEffect } from 'react';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { FaPlus, FaTrash, FaUpload, FaVolumeUp, FaImage, FaSpinner, FaCamera, FaTimes } from 'react-icons/fa';
import { extractTextFromImage, generateSentencesFromText } from '../../../utilities/extensions/imageToText';
import { translateBatchToText } from '../../../utilities/extensions/translateToText';
import Constants from '../../../utilities/constants/Constants';
import { generateBatchSpeechAudio, LanguageCodes, VoiceGenders } from '../../../utilities/extensions/textToSpeech';
import { db } from '../../../firebase';
import { generateActivityStoragePath } from '../../../utilities/storagePathHelper';

const QuestionAnswersActivity = ({ initialContent, onContentChange, chapterId, grade, selectedChapter }) => { // eslint-disable-line no-unused-vars
  const [content, setContent] = useState({
    questions: [{
      questionLanguage: '',
      questionEnglish: '',
      answerLanguage: '',
      answerEnglish: '',
      questionLanguageAudio: null,
      questionEnglishAudio: null,
      answerLanguageAudio: null,
      answerEnglishAudio: null,
      image: null,
      keywords: '',
      keyConfusingLetters: ''
    }]
  });
  const [uploadingFiles, setUploadingFiles] = useState({});
  const [playingAudio, setPlayingAudio] = useState(null); // eslint-disable-line no-unused-vars
  const [audioElements, setAudioElements] = useState({}); // eslint-disable-line no-unused-vars
  
  // Image-to-text popup state
  const [showImageToTextPopup, setShowImageToTextPopup] = useState(false);
  const [imageProcessing, setImageProcessing] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imageBlobUrl, setImageBlobUrl] = useState(null);
  const [extractedText, setExtractedText] = useState(''); // eslint-disable-line no-unused-vars
  const [generatedQuestions, setGeneratedQuestions] = useState([]);
  const [generatingAudio, setGeneratingAudio] = useState(false);
  const [translating, setTranslating] = useState(false);

  useEffect(() => {
    if (initialContent) {
      try {
        const parsed = JSON.parse(initialContent);
        setContent(parsed);
      } catch {
        // Keep default content if parsing fails
      }
    }
  }, [initialContent]);

  useEffect(() => {
    // Notify parent component of content changes
    onContentChange(content);
  }, [content, onContentChange]);

  const addQuestion = () => {
    const newQuestions = [...content.questions, {
      questionLanguage: '',
      questionEnglish: '',
      answerLanguage: '',
      answerEnglish: '',
      questionLanguageAudio: null,
      questionEnglishAudio: null,
      answerLanguageAudio: null,
      answerEnglishAudio: null,
      image: null,
      keywords: '',
      keyConfusingLetters: ''
    }];
    setContent(prev => ({ ...prev, questions: newQuestions }));
  };

  const removeQuestion = async (index) => {
    if (content.questions.length > 1) {
      const newQuestions = content.questions.filter((_, i) => i !== index);
      setContent(prev => ({ ...prev, questions: newQuestions }));
    }
  };

  const updateQuestion = (index, field, value) => {
    const newQuestions = [...content.questions];
    newQuestions[index] = { ...newQuestions[index], [field]: value };
    setContent(prev => ({ ...prev, questions: newQuestions }));
  };

  const handleFileUpload = async (index, fieldType, file) => {
    if (!file || !chapterId) return;

    const uploadKey = `${index}-${fieldType}`;
    setUploadingFiles(prev => ({ ...prev, [uploadKey]: true }));

    try {
      const storage = getStorage();
      const language = selectedChapter?.subject?.toLowerCase() || 'language';
      const gradeStr = `grade${grade}`;
      
      const filePath = generateActivityStoragePath({
        language,
        grade: gradeStr,
        chapterId,
        activityName: 'question_answers',
        fileName: file.name,
        itemId: `question${index + 1}`
      });
      
      const fileRef = ref(storage, filePath);
      const snapshot = await uploadBytes(fileRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      updateQuestion(index, fieldType, downloadURL);
    } catch (error) {
      // Handle upload error
      alert('Upload failed. Please try again.');
    } finally {
      setUploadingFiles(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const playAudio = async (audioUrl) => {
    if (!audioUrl) return;
    
    try {
      // For Firebase Storage URLs, try to extract the path and get a fresh download URL
      if (audioUrl.includes('firebasestorage.googleapis.com')) {
        // Extract the storage path from the URL
        const urlParts = audioUrl.split('/o/')[1];
        if (urlParts) {
          const storagePath = decodeURIComponent(urlParts.split('?')[0]);
          
          try {
            const storage = getStorage();
            const audioRef = ref(storage, storagePath);
            const freshDownloadURL = await getDownloadURL(audioRef);
            
            const audio = new Audio(freshDownloadURL);
            audio.play().catch(() => {});
            return;
          } catch (storageError) {
            // Fall through to direct URL attempt
          }
        }
      }
      
      // Direct URL fallback or non-Firebase URLs
      const audio = new Audio(audioUrl);
      audio.play().catch(() => {});
    } catch (error) {

        // Ignore errors
    }
  };

  const removeFile = (index, fieldType) => {
    updateQuestion(index, fieldType, null);
  };

  // Image-to-text functions
  const handleImageUploadForText = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadedImage(file);
    
    // Create blob URL for preview
    const blobUrl = URL.createObjectURL(file);
    setImageBlobUrl(blobUrl);
    
    setImageProcessing(true);
    setExtractedText('');
    setGeneratedQuestions([]);

    try {
      // Extract text from image
      const storage = getStorage();
      const text = await extractTextFromImage(file, storage, db);
      setExtractedText(text);
      
      // Generate sentences from extracted text
      const sentences = generateSentencesFromText(text);
      
      // Convert sentences to question-answer format
      const questions = sentences.map(sentence => {
        const languageText = sentence.languageText || '';
        let questionLanguage = '';
        let answerLanguage = '';
        
        // Split by question mark or colon if it exists
        if (languageText.includes('?')) {
          const parts = languageText.split('?');
          questionLanguage = parts[0].trim() + '?';
          answerLanguage = parts.slice(1).join('?').trim();
        } else if (languageText.includes(':')) {
          const parts = languageText.split(':');
          questionLanguage = parts[0].trim();
          answerLanguage = parts.slice(1).join(':').trim();
        } else {
          // If no clear separator, treat as question
          questionLanguage = languageText;
        }
        
        return {
          questionLanguage,
          questionEnglish: sentence.englishText || '',
          answerLanguage,
          answerEnglish: '',
          questionLanguageAudio: null,
          questionEnglishAudio: null,
          answerLanguageAudio: null,
          answerEnglishAudio: null,
          image: null,
          keywords: '',
          keyConfusingLetters: ''
        };
      });
      
      setGeneratedQuestions(questions);
    } catch (error) {

      alert('Failed to process image: ' + error.message);
    } finally {
      setImageProcessing(false);
    }
  };

  const addGeneratedQuestions = () => {
    if (generatedQuestions.length > 0) {
      const newQuestions = [...content.questions, ...generatedQuestions];
      setContent({ ...content, questions: newQuestions });
      closeImageToTextPopup();
    }
  };

  const closeImageToTextPopup = () => {
    setShowImageToTextPopup(false);
    setUploadedImage(null);
    if (imageBlobUrl) {
      URL.revokeObjectURL(imageBlobUrl);
      setImageBlobUrl(null);
    }
    setExtractedText('');
    setGeneratedQuestions([]);
    setImageProcessing(false);
  };

  const editGeneratedQuestion = (index, field, value) => {
    const updated = [...generatedQuestions];
    updated[index] = { ...updated[index], [field]: value };
    setGeneratedQuestions(updated);
  };

  const removeGeneratedQuestion = (index) => {
    const updated = generatedQuestions.filter((_, i) => i !== index);
    setGeneratedQuestions(updated);
  };

  const addRowAbove = (index) => {
    const newQuestion = {
      questionLanguage: '',
      questionEnglish: '',
      answerLanguage: '',
      answerEnglish: '',
      questionLanguageAudio: null,
      questionEnglishAudio: null,
      answerLanguageAudio: null,
      answerEnglishAudio: null,
      image: null,
      keywords: '',
      keyConfusingLetters: ''
    };
    const updated = [...generatedQuestions];
    updated.splice(index, 0, newQuestion);
    setGeneratedQuestions(updated);
  };

  // Translate Language questions/answers to English
  const translateLanguageText = async () => {
    if (generatedQuestions.length === 0) return;

    setTranslating(true);

    try {
      // Extract Language text that doesn't have English translations
      const textsToTranslate = [];
      const translationMap = [];

      generatedQuestions.forEach((question, index) => {
        if (question.questionLanguage && !question.questionEnglish) {
          textsToTranslate.push(question.questionLanguage);
          translationMap.push({ index, field: 'questionEnglish' });
        }
        if (question.answerLanguage && !question.answerEnglish) {
          textsToTranslate.push(question.answerLanguage);
          translationMap.push({ index, field: 'answerEnglish' });
        }
      });

      if (textsToTranslate.length === 0) {
        alert('All questions and answers already have English translations or no Language text to translate.');
        return;
      }

      // Translate the Language text (Kannada to English)
      const translatedTexts = await translateBatchToText(
        textsToTranslate, 
        db, 
        5, 
        2000, 
        Constants.LANGUAGES.KANNADA, 
        Constants.LANGUAGES.ENGLISH
      );
      
      // Update questions with English translations
      const updatedQuestions = [...generatedQuestions];
      translationMap.forEach((mapping, translationIndex) => {
        const translatedText = translatedTexts[translationIndex];
        if (translatedText && !translatedText.startsWith('[Translation Error:')) {
          updatedQuestions[mapping.index][mapping.field] = translatedText;
        }
      });
      
      setGeneratedQuestions(updatedQuestions);
    } catch (error) {

      alert('Failed to translate text: ' + error.message);
    } finally {
      setTranslating(false);
    }
  };

  // Generate audio for generated questions
  const generateAudioForQuestions = async () => {
    if (generatedQuestions.length === 0) return;

    setGeneratingAudio(true);

    try {
      const storage = getStorage();
      const ttsRequests = [];
      
      generatedQuestions.forEach((question, index) => {
        // Add Language question audio request
        if (question.questionLanguage && question.questionLanguage.trim()) {
          ttsRequests.push({
            text: question.questionLanguage,
            languageCode: LanguageCodes.KANNADA,
            ssmlGender: VoiceGenders.FEMALE,
            questionIndex: index,
            type: 'questionLanguage'
          });
        }
        
        // Add English question audio request
        if (question.questionEnglish && question.questionEnglish.trim()) {
          ttsRequests.push({
            text: question.questionEnglish,
            languageCode: LanguageCodes.ENGLISH,
            ssmlGender: VoiceGenders.FEMALE,
            questionIndex: index,
            type: 'questionEnglish'
          });
        }
        
        // Add Language answer audio request
        if (question.answerLanguage && question.answerLanguage.trim()) {
          ttsRequests.push({
            text: question.answerLanguage,
            languageCode: LanguageCodes.KANNADA,
            ssmlGender: VoiceGenders.FEMALE,
            questionIndex: index,
            type: 'answerLanguage'
          });
        }
        
        // Add English answer audio request
        if (question.answerEnglish && question.answerEnglish.trim()) {
          ttsRequests.push({
            text: question.answerEnglish,
            languageCode: LanguageCodes.ENGLISH,
            ssmlGender: VoiceGenders.FEMALE,
            questionIndex: index,
            type: 'answerEnglish'
          });
        }
      });

      // Generate audio for all requests
      const audioUrls = await generateBatchSpeechAudio(
        ttsRequests.map(req => ({
          text: req.text,
          languageCode: req.languageCode,
          ssmlGender: req.ssmlGender
        })),
        db,
        storage
      );
      
      // Map audio URLs back to questions
      const updatedQuestions = [...generatedQuestions];
      
      ttsRequests.forEach((request, requestIndex) => {
        const audioUrl = audioUrls[requestIndex];
        if (audioUrl && !audioUrl.startsWith('[TTS Error:')) {
          const { questionIndex, type } = request;
          if (type === 'questionLanguage') {
            updatedQuestions[questionIndex].questionLanguageAudio = audioUrl;
          } else if (type === 'questionEnglish') {
            updatedQuestions[questionIndex].questionEnglishAudio = audioUrl;
          } else if (type === 'answerLanguage') {
            updatedQuestions[questionIndex].answerLanguageAudio = audioUrl;
          } else if (type === 'answerEnglish') {
            updatedQuestions[questionIndex].answerEnglishAudio = audioUrl;
          }
        }
      });
      
      setGeneratedQuestions(updatedQuestions);
    } catch (error) {

      alert('Failed to generate audio: ' + error.message);
    } finally {
      setGeneratingAudio(false);
    }
  };

  // Validation function
  const validate = () => {
    const errors = [];
    
    if (!content.questions || content.questions.length === 0) {
      errors.push('At least one question is required');
    }

    content.questions.forEach((question, index) => {
      if (!question.questionLanguage?.trim() && !question.questionEnglish?.trim()) {
        errors.push(`Question ${index + 1}: At least one question (Language or English) is required`);
      }
      if (!question.answerLanguage?.trim() && !question.answerEnglish?.trim()) {
        errors.push(`Question ${index + 1}: At least one answer (Language or English) is required`);
      }
    });

    return errors;
  };

  return (
    <div className="space-y-6">
      {/* Questions Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">Question & Answer Pairs</h3>
            <p className="text-sm text-gray-600 mt-1">{content.questions.length} question{content.questions.length !== 1 ? 's' : ''} configured</p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className="flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium shadow-sm"
              onClick={() => setShowImageToTextPopup(true)}
            >
              <FaCamera className="w-4 h-4 mr-2" />
              Extract from Image
            </button>
            <button
              type="button"
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-sm"
              onClick={addQuestion}
            >
              <FaPlus className="w-4 h-4 mr-2" />
              Add Question
            </button>
          </div>
        </div>
        <div className="p-6 space-y-4">
          {content.questions.map((question, index) => (
            <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="space-y-4">
                {/* Header with Index */}
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold text-sm">
                    {index + 1}
                  </div>
                  <h3 className="text-sm font-medium text-gray-700">Question {index + 1}</h3>
                </div>
                
                {/* Line 1: Questions and Keywords */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Question Language</label>
                    <textarea
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                      placeholder="ಪ್ರಶ್ನೆ..."
                      value={question.questionLanguage || ''}
                      onChange={(e) => updateQuestion(index, 'questionLanguage', e.target.value)}
                      rows="2"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Question English</label>
                    <textarea
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                      placeholder="Question..."
                      value={question.questionEnglish || ''}
                      onChange={(e) => updateQuestion(index, 'questionEnglish', e.target.value)}
                      rows="2"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Keywords</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                      placeholder="Keywords (comma separated)..."
                      value={question.keywords || ''}
                      onChange={(e) => updateQuestion(index, 'keywords', e.target.value)}
                    />
                  </div>
                </div>
                
                {/* Line 2: Answers and Confusing Letters */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Answer Language</label>
                    <textarea
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                      placeholder="ಉತ್ತರ..."
                      value={question.answerLanguage || ''}
                      onChange={(e) => updateQuestion(index, 'answerLanguage', e.target.value)}
                      rows="2"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Answer English</label>
                    <textarea
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                      placeholder="Answer..."
                      value={question.answerEnglish || ''}
                      onChange={(e) => updateQuestion(index, 'answerEnglish', e.target.value)}
                      rows="2"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Key Confusing Letters</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                      placeholder="Confusing letters (comma separated)..."
                      value={question.keyConfusingLetters || ''}
                      onChange={(e) => updateQuestion(index, 'keyConfusingLetters', e.target.value)}
                    />
                  </div>
                </div>
                
                {/* Line 3: Audio Files and Image */}
                <div className="flex flex-wrap items-center gap-2">
                  {/* Question Language Audio */}
                  <div className="flex items-center gap-1">
                    <input
                      type="file"
                      accept="audio/*"
                      className="hidden"
                      id={`qk-audio-${index}`}
                      onChange={(e) => handleFileUpload(index, 'questionLanguageAudio', e.target.files[0])}
                      disabled={uploadingFiles[`${index}-questionLanguageAudio`]}
                    />
                    <label
                      htmlFor={`qk-audio-${index}`}
                      className="cursor-pointer px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs hover:bg-blue-100 transition-colors"
                      title="Question Language Audio"
                    >
                      🎵 QK
                    </label>
                    {uploadingFiles[`${index}-questionLanguageAudio`] && <FaSpinner className="w-3 h-3 animate-spin text-blue-600" />}
                    {question.questionLanguageAudio && !uploadingFiles[`${index}-questionLanguageAudio`] && (
                      <>
                        <button
                          type="button"
                          className="p-1 text-green-600 hover:bg-green-100 rounded"
                          onClick={() => playAudio(question.questionLanguageAudio)}
                          title="Play Question Language Audio"
                        >
                          <FaVolumeUp className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          className="p-1 text-red-600 hover:bg-red-100 rounded"
                          onClick={() => removeFile(index, 'questionLanguageAudio')}
                          title="Remove Question Language Audio"
                        >
                          <FaTrash className="w-3 h-3" />
                        </button>
                      </>
                    )}
                  </div>
                  
                  {/* Question English Audio */}
                  <div className="flex items-center gap-1">
                    <input
                      type="file"
                      accept="audio/*"
                      className="hidden"
                      id={`qe-audio-${index}`}
                      onChange={(e) => handleFileUpload(index, 'questionEnglishAudio', e.target.files[0])}
                      disabled={uploadingFiles[`${index}-questionEnglishAudio`]}
                    />
                    <label
                      htmlFor={`qe-audio-${index}`}
                      className="cursor-pointer px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs hover:bg-blue-100 transition-colors"
                      title="Question English Audio"
                    >
                      🎵 QE
                    </label>
                    {uploadingFiles[`${index}-questionEnglishAudio`] && <FaSpinner className="w-3 h-3 animate-spin text-blue-600" />}
                    {question.questionEnglishAudio && !uploadingFiles[`${index}-questionEnglishAudio`] && (
                      <>
                        <button
                          type="button"
                          className="p-1 text-green-600 hover:bg-green-100 rounded"
                          onClick={() => playAudio(question.questionEnglishAudio)}
                          title="Play Question English Audio"
                        >
                          <FaVolumeUp className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          className="p-1 text-red-600 hover:bg-red-100 rounded"
                          onClick={() => removeFile(index, 'questionEnglishAudio')}
                          title="Remove Question English Audio"
                        >
                          <FaTrash className="w-3 h-3" />
                        </button>
                      </>
                    )}
                  </div>
                  
                  {/* Answer Language Audio */}
                  <div className="flex items-center gap-1">
                    <input
                      type="file"
                      accept="audio/*"
                      className="hidden"
                      id={`ak-audio-${index}`}
                      onChange={(e) => handleFileUpload(index, 'answerLanguageAudio', e.target.files[0])}
                      disabled={uploadingFiles[`${index}-answerLanguageAudio`]}
                    />
                    <label
                      htmlFor={`ak-audio-${index}`}
                      className="cursor-pointer px-2 py-1 bg-green-50 text-green-700 rounded text-xs hover:bg-green-100 transition-colors"
                      title="Answer Language Audio"
                    >
                      🎵 AK
                    </label>
                    {uploadingFiles[`${index}-answerLanguageAudio`] && <FaSpinner className="w-3 h-3 animate-spin text-green-600" />}
                    {question.answerLanguageAudio && !uploadingFiles[`${index}-answerLanguageAudio`] && (
                      <>
                        <button
                          type="button"
                          className="p-1 text-green-600 hover:bg-green-100 rounded"
                          onClick={() => playAudio(question.answerLanguageAudio)}
                          title="Play Answer Language Audio"
                        >
                          <FaVolumeUp className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          className="p-1 text-red-600 hover:bg-red-100 rounded"
                          onClick={() => removeFile(index, 'answerLanguageAudio')}
                          title="Remove Answer Language Audio"
                        >
                          <FaTrash className="w-3 h-3" />
                        </button>
                      </>
                    )}
                  </div>
                  
                  {/* Answer English Audio */}
                  <div className="flex items-center gap-1">
                    <input
                      type="file"
                      accept="audio/*"
                      className="hidden"
                      id={`ae-audio-${index}`}
                      onChange={(e) => handleFileUpload(index, 'answerEnglishAudio', e.target.files[0])}
                      disabled={uploadingFiles[`${index}-answerEnglishAudio`]}
                    />
                    <label
                      htmlFor={`ae-audio-${index}`}
                      className="cursor-pointer px-2 py-1 bg-green-50 text-green-700 rounded text-xs hover:bg-green-100 transition-colors"
                      title="Answer English Audio"
                    >
                      🎵 AE
                    </label>
                    {uploadingFiles[`${index}-answerEnglishAudio`] && <FaSpinner className="w-3 h-3 animate-spin text-green-600" />}
                    {question.answerEnglishAudio && !uploadingFiles[`${index}-answerEnglishAudio`] && (
                      <>
                        <button
                          type="button"
                          className="p-1 text-green-600 hover:bg-green-100 rounded"
                          onClick={() => playAudio(question.answerEnglishAudio)}
                          title="Play Answer English Audio"
                        >
                          <FaVolumeUp className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          className="p-1 text-red-600 hover:bg-red-100 rounded"
                          onClick={() => removeFile(index, 'answerEnglishAudio')}
                          title="Remove Answer English Audio"
                        >
                          <FaTrash className="w-3 h-3" />
                        </button>
                      </>
                    )}
                  </div>
                  
                  {/* Image Upload */}
                  <div className="flex items-center gap-1">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      id={`img-${index}`}
                      onChange={(e) => handleFileUpload(index, 'image', e.target.files[0])}
                      disabled={uploadingFiles[`${index}-image`]}
                    />
                    <label
                      htmlFor={`img-${index}`}
                      className="cursor-pointer px-2 py-1 bg-purple-50 text-purple-700 rounded text-xs hover:bg-purple-100 transition-colors"
                      title="Upload Image"
                    >
                      📷 IMG
                    </label>
                    {uploadingFiles[`${index}-image`] && <FaSpinner className="w-3 h-3 animate-spin text-purple-600" />}
                    {question.image && !uploadingFiles[`${index}-image`] && (
                      <>
                        <button
                          type="button"
                          className="p-1 text-purple-600 hover:bg-purple-100 rounded"
                          onClick={() => window.open(question.image, '_blank')}
                          title="View Image"
                        >
                          <FaImage className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          className="p-1 text-red-600 hover:bg-red-100 rounded"
                          onClick={() => removeFile(index, 'image')}
                          title="Remove Image"
                        >
                          <FaTrash className="w-3 h-3" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
                
                {/* Remove Question Button */}
                {content.questions.length > 1 && (
                  <div className="flex justify-end">
                    <button
                      type="button"
                      className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                      onClick={() => removeQuestion(index)}
                      title="Remove Question"
                    >
                      <FaTrash className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Validation Display */}
      {(() => {
        const errors = validate();
        if (errors.length > 0) {
          return (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <div className="flex items-start">
                <div className="w-6 h-6 text-amber-600 mr-3 mt-0.5">
                  <svg fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-amber-800 mb-2">Please fix the following issues:</h3>
                  <ul className="space-y-1">
                    {errors.map((error, index) => (
                      <li key={index} className="flex items-start text-amber-700">
                        <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-2 mr-2 flex-shrink-0"></span>
                        <span>{error}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          );
        }
        return null;
      })()}

      {/* Image-to-Text Popup */}
      {showImageToTextPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Extract Questions from Image</h3>
                <p className="text-sm text-gray-600 mt-1">Upload an image to automatically extract question-answer pairs</p>
              </div>
              <button
                onClick={closeImageToTextPopup}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6">
              {!uploadedImage ? (
                /* Upload Section */
                <div className="text-center">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 hover:border-emerald-400 transition-colors">
                    <div className="flex flex-col items-center">
                      <FaCamera className="w-12 h-12 text-gray-400 mb-4" />
                      <label htmlFor="imageToTextUpload" className="text-lg font-medium text-gray-900 mb-2 cursor-pointer">
                        Upload an image to extract questions
                      </label>
                      <input
                        type="file"
                        id="imageToTextUpload"
                        className="hidden"
                        accept="image/*"
                        onChange={handleImageUploadForText}
                        disabled={imageProcessing}
                      />
                      <button
                        type="button"
                        className="bg-emerald-500 hover:bg-emerald-600 text-white font-medium py-3 px-6 rounded-lg transition-colors disabled:bg-emerald-400 disabled:cursor-not-allowed mb-3"
                        onClick={() => document.getElementById('imageToTextUpload').click()}
                        disabled={imageProcessing}
                      >
                        Choose Image File
                      </button>
                      <p className="text-sm text-gray-500">
                        Supported formats: JPG, PNG, GIF. The AI will analyze the image and extract question-answer pairs.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                /* Processing and Results Section */
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Uploaded Image */}
                  <div className="lg:col-span-1">
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <FaImage className="text-blue-600" />
                        Uploaded Image
                      </h4>
                      <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                        <img 
                          src={imageBlobUrl} 
                          alt="Uploaded" 
                          className="w-full h-auto rounded-lg shadow-sm"
                          style={{ maxHeight: '300px', objectFit: 'contain' }}
                        />
                      </div>
                      <button 
                        className="w-full mt-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg border border-gray-300 transition-colors flex items-center justify-center gap-2 text-sm"
                        onClick={() => {
                          closeImageToTextPopup();
                        }}
                      >
                        <FaUpload className="w-4 h-4" />
                        Upload Different Image
                      </button>
                    </div>
                  </div>
                  
                  {/* Generated Questions */}
                  <div className="lg:col-span-2">
                    {imageProcessing ? (
                      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                        <div className="flex flex-col items-center gap-4">
                          <FaSpinner className="w-12 h-12 animate-spin text-emerald-500" />
                          <div>
                            <h4 className="text-lg font-semibold text-gray-900 mb-1">Processing Image</h4>
                            <p className="text-gray-600 text-sm">Analyzing image and extracting questions using AI...</p>
                          </div>
                        </div>
                      </div>
                    ) : generatedQuestions.length > 0 ? (
                      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
                          <div>
                            <p className="text-sm font-medium text-gray-700 flex items-center gap-2">
                              ✨ Generated questions ({generatedQuestions.length})
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <button 
                              className="bg-purple-500 hover:bg-purple-600 text-white font-medium py-2 px-4 rounded-lg transition-colors disabled:bg-purple-400 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
                              onClick={translateLanguageText}
                              disabled={translating || generatingAudio}
                            >
                              {translating ? (
                                <>
                                  <FaSpinner className="w-4 h-4 animate-spin" />
                                  Translating...
                                </>
                              ) : (
                                <>
                                  🌐 Translate Text
                                </>
                              )}
                            </button>
                            <button 
                              className="bg-orange-500 hover:bg-orange-600 text-white font-medium py-2 px-4 rounded-lg transition-colors disabled:bg-orange-400 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
                              onClick={generateAudioForQuestions}
                              disabled={generatingAudio || translating}
                            >
                              {generatingAudio ? (
                                <>
                                  <FaSpinner className="w-4 h-4 animate-spin" />
                                  Generating Audio...
                                </>
                              ) : (
                                <>
                                  🔊 Generate Audio
                                </>
                              )}
                            </button>
                            <button 
                              className="bg-green-500 hover:bg-green-600 text-white font-medium py-2 px-4 rounded-lg transition-colors flex items-center gap-2 text-sm"
                              onClick={addGeneratedQuestions}
                            >
                              <FaPlus className="w-4 h-4" />
                              Add All Questions
                            </button>
                          </div>
                        </div>
                        
                        <div className="space-y-4 max-h-96 overflow-y-auto">
                          {generatedQuestions.map((question, index) => (
                            <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Question Language</label>
                                  <textarea
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                    value={question.questionLanguage}
                                    onChange={(e) => editGeneratedQuestion(index, 'questionLanguage', e.target.value)}
                                    placeholder="Language question..."
                                    rows="2"
                                  />
                                  {question.questionLanguageAudio && (
                                    <div className="mt-2">
                                      <button
                                        type="button"
                                        className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700 hover:bg-green-50 px-2 py-1 rounded transition-colors"
                                        onClick={() => playAudio(question.questionLanguageAudio)}
                                      >
                                        <FaVolumeUp className="w-3 h-3" />
                                        Play Audio
                                      </button>
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Question English</label>
                                  <textarea
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                    value={question.questionEnglish}
                                    onChange={(e) => editGeneratedQuestion(index, 'questionEnglish', e.target.value)}
                                    placeholder="English question..."
                                    rows="2"
                                  />
                                  {question.questionEnglishAudio && (
                                    <div className="mt-2">
                                      <button
                                        type="button"
                                        className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700 hover:bg-green-50 px-2 py-1 rounded transition-colors"
                                        onClick={() => playAudio(question.questionEnglishAudio)}
                                      >
                                        <FaVolumeUp className="w-3 h-3" />
                                        Play Audio
                                      </button>
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Answer Language</label>
                                  <textarea
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                    value={question.answerLanguage}
                                    onChange={(e) => editGeneratedQuestion(index, 'answerLanguage', e.target.value)}
                                    placeholder="Language answer..."
                                    rows="2"
                                  />
                                  {question.answerLanguageAudio && (
                                    <div className="mt-2">
                                      <button
                                        type="button"
                                        className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700 hover:bg-green-50 px-2 py-1 rounded transition-colors"
                                        onClick={() => playAudio(question.answerLanguageAudio)}
                                      >
                                        <FaVolumeUp className="w-3 h-3" />
                                        Play Audio
                                      </button>
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Answer English</label>
                                  <textarea
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                    value={question.answerEnglish}
                                    onChange={(e) => editGeneratedQuestion(index, 'answerEnglish', e.target.value)}
                                    placeholder="English answer..."
                                    rows="2"
                                  />
                                  {question.answerEnglishAudio && (
                                    <div className="mt-2">
                                      <button
                                        type="button"
                                        className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700 hover:bg-green-50 px-2 py-1 rounded transition-colors"
                                        onClick={() => playAudio(question.answerEnglishAudio)}
                                      >
                                        <FaVolumeUp className="w-3 h-3" />
                                        Play Audio
                                      </button>
                                    </div>
                                  )}
                                </div>
                                
                                {/* Additional Fields */}
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Keywords</label>
                                  <input
                                    type="text"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                    value={question.keywords}
                                    onChange={(e) => editGeneratedQuestion(index, 'keywords', e.target.value)}
                                    placeholder="Keywords (comma separated)..."
                                  />
                                </div>
                                
                                <div>
                                  <label className="block text-xs font-medium text-gray-700 mb-1">Key Confusing Letters</label>
                                  <input
                                    type="text"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                                    value={question.keyConfusingLetters}
                                    onChange={(e) => editGeneratedQuestion(index, 'keyConfusingLetters', e.target.value)}
                                    placeholder="Confusing letters (comma separated)..."
                                  />
                                </div>
                              </div>
                              <div className="flex items-center justify-between">
                                <button 
                                  className="text-blue-500 hover:text-blue-700 hover:bg-blue-50 p-2 rounded-lg transition-colors"
                                  onClick={() => addRowAbove(index)}
                                  title="Add question above this one"
                                >
                                  <FaPlus className="w-4 h-4" />
                                </button>
                                <button 
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition-colors"
                                  onClick={() => removeGeneratedQuestion(index)}
                                  title="Remove this question"
                                >
                                  <FaTrash className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
                        <div className="flex flex-col items-center gap-3">
                          <span className="text-4xl">📝</span>
                          <div>
                            <h4 className="text-lg font-semibold text-blue-900 mb-1">No questions generated</h4>
                            <p className="text-blue-700 text-sm">The image processing didn&apos;t generate questions. Try uploading a different image with clearer text.</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Validation function
export const validateQuestionAnswersContent = (content) => {
  const errors = [];
  
  if (!content || typeof content !== 'object') {
    errors.push('Content must be an object');
    return errors;
  }
  
  if (!content.questions || !Array.isArray(content.questions)) {
    errors.push('Questions must be an array');
    return errors;
  }
  
  if (content.questions.length === 0) {
    errors.push('At least one question is required');
    return errors;
  }
  
  content.questions.forEach((question, index) => {
    const questionNum = index + 1;
    
    if (!question.questionLanguage && !question.questionEnglish) {
      errors.push(`Question ${questionNum}: At least one question (Language or English) is required`);
    }
    
    if (!question.answerLanguage && !question.answerEnglish) {
      errors.push(`Question ${questionNum}: At least one answer (Language or English) is required`);
    }
    
    // Validate that if we have Language question, we should have Language answer and vice versa
    if (question.questionLanguage && !question.answerLanguage) {
      errors.push(`Question ${questionNum}: Language answer is required when Language question is provided`);
    }
    
    if (question.questionEnglish && !question.answerEnglish) {
      errors.push(`Question ${questionNum}: English answer is required when English question is provided`);
    }
    
    // Validate English text: must start with capital letter
    if (question.questionEnglish?.trim()) {
      const trimmed = question.questionEnglish.trim();
      if (!/^[A-Z]/.test(trimmed)) {
        errors.push(`Question ${questionNum}: English question must start with a capital letter`);
      }
    }
    if (question.answerEnglish?.trim()) {
      const trimmed = question.answerEnglish.trim();
      if (!/^[A-Z]/.test(trimmed)) {
        errors.push(`Question ${questionNum}: English answer must start with a capital letter`);
      }
    }
  });
  
  return errors;
};

export default QuestionAnswersActivity;
